import { getCryptoPro } from '@/libs/crypto_pro/index'
import { DigitalSignatureCurrentStatus } from '@/libs/digital_signature/current_status'
import { $ } from '@/libs/jquery/index'
import { routes } from '@/libs/routes/index'
import { UCLogger } from '@/utilities/uc_logger'
import { baseSelector } from '@/views/crm/leads/etp_gpb_consulting/shared/edit/form/index.ts'
import cyrillicToTranslit from '@frontend/cyrillic-to-translit-js'
import { Base64 } from 'js-base64'

interface IGetOption {
  ucCurrentStatus: DigitalSignatureCurrentStatus
  statusChangehandler(): void
}

export class DigitalSignatureGetCertificate {
  public base: JQuery = null

  public ucCurrentStatus: DigitalSignatureCurrentStatus
  public statusChangehandler: () => void
  public leadID: number
  public exportPolicy: number

  private selectors: ISelectors = {
    base: baseSelector,
    certList: '[data-selector="digitalSignature__select"]',
    getCertificateButton: `[data-uc-action="user_confirmed"], [data-uc-action="user_registered"],
      [data-uc-action="cert_error"]`,
    popup: '[data-selector="digitalSignature__popup"]',
    writeCertificateButton: '[data-uc-action="cert_confirmed"]',
  }

  constructor(options: IGetOption) {
    this.base = $(this.selectors.base)

    if (this.base.length) {
      const { 
        ucCurrentStatus,
        statusChangehandler,
      } = options

      this.ucCurrentStatus = ucCurrentStatus
      this.statusChangehandler = statusChangehandler
      this.exportPolicy = 0

      this.init()
    }
  }

  public init(): void {
    this.setLeadID()
    this.base
      .on('click', this.selectors.writeCertificateButton, (e) => this.installCertificate(e))
      .on('click', this.selectors.getCertificateButton, (e) => this.requestCertificate(e))
  }

  public certificateTitle(params): string {
    const email = params.notificationEmail
    const fio = params.description.replace('Запрос на создание и подтверждения сертификата: ', '')
    const fioInLatin = new cyrillicToTranslit().transform(`${ fio } ${ email }`, '_')

    return `${ fioInLatin }_${ Date.now() }`
  }

  private installCertificate(e: JQuery.TriggeredEvent): void {
    const btn = $(e.target)
    btn.attr('disabled', 'disabled')

    const url = routes.crm_verification_center_certificates_path({ lead_id: this.leadID })

    $.get(url).then((data) => {
      getCryptoPro().then((CryptoPro) => {
        CryptoPro
          .call('installCertificate', data.raw_certificate)
          .then(() => {
            this.ucCurrentStatus.set('Сертификат установлен')
            this.statusChangehandler()
            UCLogger.notice({
              lead_id: this.leadID.toString(),
              message: 'установка завершена',
              status: 'success',
            })
            btn.removeAttr('disabled')
          }).catch((err) => {
            this.ucCurrentStatus.setError('Ошибка установки сертификата')
            alert(err)
            UCLogger.notice({
              lead_id: this.leadID.toString(),
              message: 'ошибка установки сертификата',
              status: 'error',
            })
            btn.removeAttr('disabled')
          })
      })
    }).fail((err) => {
      btn.removeAttr('disabled')
    })
  }

  private requestCertificate(e: JQuery.TriggeredEvent): void {
    const btn = $(e.target)
    btn.attr('disabled', 'disabled')

    const recipient = btn.data('recipient')
    const tariff = btn.data('tariff')

    this.getUserParams().done((data) => {
      const response = data.rdn
      const template_id = data.template_id
      const certificateName = this.certificateTitle(data.rdn.parameters)
      const exportPolicy = this.exportPolicy

      const nameValues = (recipient === 'Юридическое лицо' && tariff === 'transition')
        ? { CpRaExpirationDate: '20221231235959+03:00', exportPolicy }
        : { exportPolicy }

      // tslint:disable-next-line no-console
      console.log('OID: ', nameValues)

      response.parameters.waitAsyncOperations = 'true'
      // Подпись, подтверждающая подачу запроса на ЦР
      const dataToSign = response.parameters.notSignedRequest

      this.privateKey(dataToSign, template_id, certificateName, nameValues).then(({ certificate, author }) => {
        const params = {
          certHASH: this.base.find(this.selectors.certList).val(),
          dataToSign: null,
        }

        params.dataToSign = certificate
        getCryptoPro().then((CryptoPro) => {
          CryptoPro
            .call('signData', params.certHASH, params.dataToSign, false)
            .then((certificate2) => {
              params.dataToSign = certificate2
              CryptoPro
                .call('signData', params.certHASH, params.dataToSign, false)
                .then((certificate3) => {
                  params.dataToSign = certificate3
                  CryptoPro
                    .call('signData', params.certHASH, params.dataToSign, false)
                    .then((certificate4) => {
                      response.parameters.rawSignedCertRequest = certificate4
                      params.dataToSign = Base64.encode(JSON.stringify(response.parameters))

                      CryptoPro
                        .call('signData', params.certHASH, params.dataToSign, false)
                        .then((signedParameters) => {
                          response.signed_parameters = signedParameters
                          /* tslint:disable-next-line variable-name */
                          const register_user_data: TUnknown = { request_body: response }
                          if (author === 'user') {
                            register_user_data.operator_certificate = params.certHASH
                          }

                          this.ucCurrentStatus.set('Ожидается создание сертификата в УЦ')
                          const url = routes.crm_verification_center_certificates_path( this.leadID )

                          this.sendCreationRequest({url, register_user_data, btn})
                        })
                    })
                })
            })
        })
      })
    })
  }

  private privateKey(dataToSign, templateId, certificateName, nameValues): Promise<TUnknown> {
    return new Promise((resolve, reject) => {
      $.get(routes.crm_verification_center_uc_request_path({lead_id: this.leadID})).then((data) => {
        const userPresavedData = data

        if (userPresavedData && userPresavedData.request_body && userPresavedData.request_body.length > 0) {
          resolve({ certificate: userPresavedData.request_body, author: 'user'})
        } else {
          getCryptoPro().then((CryptoPro) => {
            CryptoPro
              .call('createCertificateRequest', dataToSign, templateId, certificateName, nameValues)
              .then((certificate) => {
                resolve({ certificate, author: 'operator' })
              })
          })
        }
      })
    })
  }

  private setLeadID(): void {
    this.leadID = this.base.find(this.selectors.popup).data('lead-id')
  }

  private getUserParams(): JQuery.jqXHR {
    const url = routes.new_crm_verification_center_certificate_path({ lead_id: this.leadID })

    return $.get(url)
  }

  private sendCreationRequest({url, register_user_data, btn}): void {
    $
      .post(url, register_user_data)
      .then(() => {
        this.showGetCertificate()
        btn.removeAttr('disabled')
      })
      .fail((res) => {
        this.showError(res)
        btn.removeAttr('disabled')
      })
  }

  private showGetCertificate(): void {
    this.ucCurrentStatus.set('Сертификат успешно создан в УЦ')
    this.statusChangehandler()
  }

  private showError(response): void {
    this.ucCurrentStatus.setError('Ошибка создания сертификата в УЦ')
    const errorMessage = response.responseJSON.error
    alert(`Ошибка: ${ errorMessage }`)
  }
}
